import React from 'react';
import { FleetOverview, FleetOverviewRun, FleetEscalation } from '../../services/fleetHudApi';
import { formatElapsedSeconds, formatElapsedSince } from './fleetTime';
// ACTIVE_STATUSES — single source of truth shared by NowCard, deriveMood, and FleetHud.
// Imported via fleet barrel for dead-code analyzer visibility.
import { ACTIVE_STATUSES } from './';

function isActiveRun(run: FleetOverviewRun | null | undefined): boolean {
  return !!run && !!run.status && ACTIVE_STATUSES.has(run.status);
}

interface NowCardProps {
  overview: FleetOverview | null;
  escalations: FleetEscalation[];
}

export default function NowCard({ overview, escalations }: NowCardProps) {
  if (!overview) {
    return (
      <section className="rounded-lg border border-hairline bg-surface p-5">
        <p className="text-sm text-text-muted">Contacting fleet…</p>
      </section>
    );
  }

  const alive = overview.orchestrator_alive === true;
  const journalAge = formatElapsedSeconds(overview.journal_age_seconds);
  const run = overview.latest_run;
  const active = isActiveRun(run);
  const openEscalations =
    typeof overview.open_escalations === 'number' ? overview.open_escalations : escalations.length;

  return (
    <section className="rounded-lg border border-hairline bg-surface p-5">
      {/* Status pill — the dot is one of only two animate-pulse elements on the page */}
      <div className="flex items-center gap-2">
        <span
          className={`inline-block h-2 w-2 rounded-full ${
            alive ? 'bg-green-500 animate-pulse' : 'bg-red-500'
          }`}
        />
        <span className={`text-sm font-medium ${alive ? 'text-text-primary' : 'text-red-500'}`}>
          {alive ? `FLEET LIVE — journal ${journalAge} ago` : `FLEET DOWN — last journal ${journalAge} ago`}
        </span>
      </div>

      {active && run ? (
        <div className="mt-4 space-y-2">
          <div className="flex items-baseline justify-between gap-4">
            <span className="font-mono text-sm text-text-secondary">
              {run.run_id}
            </span>
            <span className="font-mono text-sm text-text-primary text-right">
              item {run.current_phase ?? '—'}/{run.total_phases ?? '—'}
            </span>
          </div>
          {/* Active stage row — animate-pulse on the left-border indicator only */}
          <div className="flex items-center gap-3">
            <span className="inline-block w-0.5 self-stretch bg-brand animate-pulse" />
            <span className="text-sm text-text-primary">
              PHASE {run.current_phase ?? '—'} — running {formatElapsedSince(run.created_at)}
            </span>
            <span className="text-xs uppercase tracking-wide text-text-muted">
              {run.status}
            </span>
          </div>
          {(run.items_summary ?? [])
            .filter((it) => it.pr_url || it.commit)
            .map((it, i) => (
              <div key={i} className="flex items-center gap-3 pl-3.5 font-mono text-xs">
                {it.pr_url ? (
                  <a
                    href={it.pr_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand hover:underline"
                  >
                    PR #{it.pr_url.split('/').pop()} ↗
                  </a>
                ) : null}
                {it.commit ? <span className="text-text-muted">{it.commit.slice(0, 8)}</span> : null}
              </div>
            ))}
        </div>
      ) : openEscalations === 0 ? (
        <div className="py-12 text-center">
          <p className="font-heading text-2xl text-text-muted tracking-wide">
            NOTHING NEEDS YOU
          </p>
        </div>
      ) : null}
    </section>
  );
}
